import { FC, useContext, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useApi } from "../../http/api";
import { useClaimAttachmentsApi } from "../../http/claimAttachments/api";
import { ClaimAttachment, RenameClaimAttachment } from "../../http/claimAttachments/types";
import { isConflict } from "../../http/errors";
import { PaginationQuery } from "../../types";
import { formatTimeStamp, isEmbed, isImage, isMobile, isVideo } from "../../types/utils";
import SvgEdit from "../icons/Edit";
import DownloadLink from "../ui/DownloadLink";
import FileIcon from "../ui/FileIcon";
import { Action, getDeleteAction, getDownloadAction, getPreviewAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import PreviewFileDialog from "../ui/PreviewFileDialog";
import Table from "../ui/Table";
import { FileToOverwrite, type TableUploadFileResult } from "../ui/TableFileUploadDialog";
import DeleteClaimAttachmentDialog from "./DeleteClaimAttachmentDialog";
import UploadClaimAttachment from "./UploadClaimAttachment";

type ClaimAttachmentsProps = {
  setIsDragging: (isDragging: boolean) => void;
};

const ClaimAttachments: FC<ClaimAttachmentsProps> = ({ setIsDragging }) => {
  const i18n = useI18n();
  const claimAttachmentsApi = useClaimAttachmentsApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const { id: claimId } = useParams<{ id: string }>();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [itemToPreview, setItemToPreview] = useState<{ name: string; url: string }>();
  const [itemToDelete, setItemToDelete] = useState<ClaimAttachment>();
  const [documentIdToRename, setDocumentIdToRename] = useState<string>("");
  const [fileToOverwrite, setFileToOverwrite] = useState<FileToOverwrite>();
  const [isOverwritingFile, setIsOverwritingFile] = useState(false);
  const api = useApi();
  const canUploadDocuments = userSettings.permissions.claims.insert;

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const result = claimAttachmentsApi.useClaimAttachments(query, aborter.current.signal, claimId!);
  const [data] = useDataInitializer(result.data, result.error);

  const uploadClaimAttachment = async (file: File): Promise<TableUploadFileResult> => {
    try {
      await claimAttachmentsApi.insertClaimAttachment(file, false, claimId);
    } catch (error) {
      if (!isConflict(error)) {
        throw error;
      }

      return new Promise<TableUploadFileResult>((resolve, reject) => {
        setFileToOverwrite({
          file,
          onCancel: () => {
            setFileToOverwrite(undefined);
            resolve("skipped");
          },
          onOverwrite: () => {
            setIsOverwritingFile(true);
            claimAttachmentsApi
              .insertClaimAttachment(file, true, claimId)
              .then(() => {
                setFileToOverwrite(undefined);
                resolve("success");
              })
              .catch((error) => {
                setFileToOverwrite(undefined);
                reject(error);
              })
              .finally(() => setIsOverwritingFile(false));
          },
        });
      });
    }
  };

  const onSubmit = async (id: string, name: string, callback: () => void): Promise<void> => {
    const body: RenameClaimAttachment = { name };

    try {
      await claimAttachmentsApi.renameClaimAttachment(id, body, claimId);
      callback();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
  };

  const downloadAttachment = (item: ClaimAttachment) =>
    api
      .downloadFile(`/api/admin/claim_attachments/${item.id}` + `?t=${new Date().getTime()}`, item.name)
      .catch((err) => dispatch(showErrorNotification(i18n, err)));

  const onPreviewClick = async (item: ClaimAttachment) => {
    if (!isMobile && (isVideo(item.name) || isImage(item.name) || isEmbed(item.name))) {
      const docUrl = await api.createObjectURL(
        `/api/admin/claim_attachments/${item.id}` + `?t=${new Date().getTime()}`,
        true,
      );
      setItemToPreview({ name: item.name, url: docUrl });
      return;
    }
    downloadAttachment(item);
  };

  const getRowActions = (item: ClaimAttachment): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.claims.read) {
      actions.push(getDownloadAction(i18n, () => downloadAttachment(item)));
    }

    if (
      userSettings.permissions.claims.read &&
      !isMobile &&
      (isVideo(item.name) || isImage(item.name) || isEmbed(item.name))
    ) {
      actions.push(getPreviewAction(i18n, () => onPreviewClick(item)));
    }

    if (userSettings.permissions.claims.update) {
      actions.push({
        title: i18n.t.common.rename,
        onClick: () => setDocumentIdToRename(item.id),
        icon: SvgEdit,
      });
    }

    if (userSettings.permissions.claims.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), !!actions.length));
    }

    return actions;
  };

  return (
    <>
      <Table<ClaimAttachment>
        filters={filters}
        fileDrop={{
          enabled: canUploadDocuments,
          onUploadFile: uploadClaimAttachment,
          isDraggingOn: setIsDragging,
        }}
        buttons={{
          main: canUploadDocuments && !!claimId && <UploadClaimAttachment claimId={claimId} />,
        }}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        head={
          <>
            <th align="left" style={{ width: 1 }}></th>
            <th align="left">{i18n.t.common.name}</th>
            <th align="left">{i18n.t.common.uploadedBy}</th>
            <th align="left">{i18n.t.common.uploadedAt}</th>
            <th align="left">{i18n.t.common.modifiedBy}</th>
            <th align="left">{i18n.t.common.modifiedAt}</th>
            <th></th>
          </>
        }
        renderRow={(item) => (
          <>
            <Table.IconTd>
              <FileIcon fileName={item.name} />
            </Table.IconTd>
            <Table.EditableCell
              defaultName={item.name}
              itemId={item.id}
              onSubmit={onSubmit}
              onCancel={() => setDocumentIdToRename("")}
              isEditing={item.id === documentIdToRename}
              disabled={!userSettings.permissions.claims.update}
            >
              <DownloadLink url={`/api/admin/claim_attachments/${item.id}`} fileName={item.name}>
                {item.name}
              </DownloadLink>
            </Table.EditableCell>
            <td>{item.uploader.fullName}</td>
            <td>{formatTimeStamp(item.uploadedAt, i18n.language)}</td>
            <td>{item.editor?.fullName ?? ""}</td>
            <td>{item.editor && item.modifiedAt ? formatTimeStamp(item.modifiedAt, i18n.language) : ""}</td>
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!fileToOverwrite && (
        <ModalDialog
          title={i18n.t.documents.alreadyPresent}
          isSubmitting={isOverwritingFile}
          onClose={() => {
            if (!isOverwritingFile) {
              fileToOverwrite.onCancel();
            }
          }}
          actions={[
            {
              title: i18n.t.common.cancel,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onCancel,
              type: "secondary",
            },
            {
              title: i18n.t.common.overwrite,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onOverwrite,
            },
          ]}
        >
          <p>{fileToOverwrite.file.name}</p>
          {i18n.t.documents.questions.overwrite}
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <DeleteClaimAttachmentDialog claimAttachment={itemToDelete} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToPreview && (
        <PreviewFileDialog
          fileName={itemToPreview.name}
          onClose={() => setItemToPreview(undefined)}
          docUrl={itemToPreview.url}
        />
      )}
    </>
  );
};

export default ClaimAttachments;
